import React, { useEffect, useState } from "react";
import { Plus, Trash2, CalendarClock, UserCheck, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "../../../components/ui/checkbox";
import { api } from "../../../lib/api";
import { useFb, inp, card, btn, btnMain, localToday, Label, Empty } from "./shared";

const Summary = () => {
  const [s, setS] = useState({ posts: 0, posted: 0, due: 0 });
  useEffect(() => {
    const today = localToday();
    Promise.all([api.get("/admin/fb/schedule"), api.get("/admin/fb/followups")]).then(([a, b]) => {
      const list = a.data.filter((x) => x.date === today);
      setS({ posts: list.length, posted: list.filter((x) => x.posted).length, due: b.data.filter((x) => !x.done && x.due_at && new Date(x.due_at) <= new Date()).length });
    }).catch(() => {});
  }, []);
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className={`${card} flex items-center gap-3`} data-testid="fb-tasks-posts-today"><CalendarClock className="text-[#0e8c7f]" /><div><div className="text-[20px] font-bold">{s.posted}/{s.posts}</div><div className="text-[12.5px] text-[#6b7c79]">posts done today</div></div></div>
      <div className={`${card} flex items-center gap-3`} data-testid="fb-tasks-followups-due"><UserCheck className="text-[#f59e0b]" /><div><div className="text-[20px] font-bold">{s.due}</div><div className="text-[12.5px] text-[#6b7c79]">follow-ups due now</div></div></div>
    </div>
  );
};

const SummaryNow = () => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/admin/fb/summary/send"); toast.success("Summary email sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };
  return (
    <div className={`${card} flex flex-wrap items-center gap-3`} data-testid="fb-summary-box">
      <Mail className="text-[#0e8c7f]" size={20} />
      <span className="flex-1 text-[14px] text-[#3d504d]">A summary of today's posts, follow-ups and tasks is emailed to your alert email at <b>8:00 AM</b> and <b>5:00 PM</b> Eastern.</span>
      <button onClick={send} disabled={busy} className={btn} data-testid="fb-summary-send">{busy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}Send it now</button>
    </div>
  );
};

export default function TasksSection() {
  const { items, add, update, remove } = useFb("tasks");
  const [f, setF] = useState({ title: "", time: "09:00", repeat: true, date: "" });
  const today = localToday();
  const list = items.filter((t) => t.repeat || t.date === today).sort((a, b) => (a.time || "99").localeCompare(b.time || "99"));
  const later = items.filter((t) => !t.repeat && t.date > today).sort((a, b) => a.date.localeCompare(b.date));
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Add a task");
    await add({ ...f, date: f.repeat ? "" : (f.date || today) });
    setF({ ...f, title: "" });
  };
  const Row = ({ t }) => {
    const done = t.done_on === today;
    return (
      <div className="flex items-center gap-3 border-b border-[#eef4f3] py-2.5 last:border-0" data-testid={`fb-task-${t.id}`}>
        <Checkbox checked={done} onCheckedChange={(v) => update(t.id, { done_on: v ? today : "" })} data-testid={`fb-task-check-${t.id}`} />
        <span className="w-14 text-[13px] font-semibold text-[#0e8c7f]">{t.time}</span>
        <span className={`flex-1 text-[14.5px] ${done ? "text-[#9aaba8] line-through" : "text-[#0f2e2a]"}`}>{t.title}</span>
        <span className="text-[11.5px] text-[#8a9a97]">{t.repeat ? "Every day" : t.date}</span>
        <button onClick={() => remove(t.id)} className="text-[#9aaba8] hover:text-[#dc2626]" aria-label="Delete task" data-testid={`fb-task-delete-${t.id}`}><Trash2 size={14} /></button>
      </div>
    );
  };
  return (
    <div className="space-y-4" data-testid="fb-tasks-section">
      <Summary />
      <SummaryNow />
      <div className={card}>
        <h3 className="mb-2 text-[15px] font-bold text-[#0f2e2a]">Today · {list.filter((t) => t.done_on === today).length}/{list.length} done</h3>
        {list.length === 0 ? <Empty testid="fb-tasks-empty">No tasks for today.</Empty> : list.map((t) => <Row key={t.id} t={t} />)}
        {later.length > 0 && <><h3 className="mb-1 mt-4 text-[13px] font-bold uppercase text-[#6b7c79]">Coming up</h3>{later.map((t) => <Row key={t.id} t={t} />)}</>}
      </div>
      <div className={`${card} grid gap-3 md:grid-cols-6`}>
        <Label t="Task" className="md:col-span-3"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} className={inp} data-testid="fb-task-new-title" /></Label>
        <Label t="Time"><input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className={inp} data-testid="fb-task-new-time" /></Label>
        <Label t="When">
          <select value={f.repeat ? "daily" : "once"} onChange={(e) => setF({ ...f, repeat: e.target.value === "daily" })} className={inp} data-testid="fb-task-new-repeat"><option value="daily">Every day</option><option value="once">One day</option></select>
        </Label>
        {!f.repeat ? <Label t="Date"><input type="date" value={f.date || today} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} data-testid="fb-task-new-date" /></Label>
          : <div className="flex items-end"><button onClick={submit} className={`${btnMain} w-full justify-center`} data-testid="fb-task-add"><Plus size={15} />Add</button></div>}
        {!f.repeat && <div className="md:col-span-6"><button onClick={submit} className={btnMain} data-testid="fb-task-add"><Plus size={15} />Add</button></div>}
      </div>
      <p className="text-[12.5px] text-[#6b7c79]">Daily tasks reset every morning. <button onClick={() => items.forEach((t) => t.done_on === today && update(t.id, { done_on: "" }))} className={`${btn} !py-0.5`} data-testid="fb-task-reset">Uncheck all</button></p>
    </div>
  );
}
